/**
 * The first-quest draft — the "unsaved-state handling" half of AD-04's quest clause (#576 o6).
 *
 * A parent part-way through naming their family's first quest has done work. Without this, a
 * remount, a step change or a refresh loses it silently, which is the same defect the child draft
 * and the host draft each exist to close.
 *
 * This is deliberately the SAME shape as `childDraft.js` and `hostDraft.js`, and not a
 * generalisation of them. They differ where it matters — a different key, a different allowlist, a
 * different clearing rule — and one module serving all three would carry every difference as a
 * parameter. The rules that make any of them safe are repeated here on purpose:
 *
 *   1. ONLY THE KNOWN FIELDS ARE STORED, on the way in AND on the way out.
 *   2. EVERY FIELD IS CHECKED FOR WHAT IT MUST BE. `periodType` is checked against the two values
 *      the form offers rather than merely for being a string, because `createChoreSchema` accepts
 *      only `hourly | daily | weekly | monthly` and a stored `"fortnightly"` would be sent straight
 *      to the server and refused — with the family left looking at a form that seems fine.
 *   3. EVERY ENTRY POINT IS TOTAL. Storage can be denied and stored bytes can be corrupt.
 *   4. THE DRAFT IS SCOPED TO THE FAMILY THAT WROTE IT, because `sessionStorage` outlives a
 *      sign-out within the same tab and this app clears none of it on the way out.
 *
 * `sessionStorage`, not `localStorage`: a draft is a convenience for one sitting, not a record.
 */

/** @type {string} The key prefix. The tenant id is appended, so one family cannot read another's. */
export const QUEST_DRAFT_KEY_PREFIX = 'chorestory.onboarding.quest';

/** The only periods the form offers, and the only ones this module will hand back. */
export const QUEST_PERIODS = Object.freeze(['daily', 'weekly']);

/** An empty draft, which is also what a denied, absent or corrupt read returns. */
export const EMPTY_QUEST_DRAFT = Object.freeze({ name: '', periodType: 'daily' });

/**
 * The storage key for one family.
 *
 * @param {string} tenantId the family's own id
 * @returns {string|null} the key, or `null` when there is no usable tenant id
 */
export function questDraftKey(tenantId) {
  if (typeof tenantId !== 'string' || tenantId.trim() === '') return null;
  return `${QUEST_DRAFT_KEY_PREFIX}.${tenantId}`;
}

/** Keep the allowlisted fields, and keep each one ONLY when it is what it must be. */
function pick(source) {
  const out = {};
  if (!source || typeof source !== 'object' || Array.isArray(source)) return out;
  if (typeof source.name === 'string') out.name = source.name;
  if (QUEST_PERIODS.includes(source.periodType)) out.periodType = source.periodType;
  // #577 row 3. `createChoreSchema` takes `reward: z.number().min(0)`, so only a finite, non-negative
  // NUMBER is kept. A string "1.50" or a negative would be sent and refused behind a form that looked
  // fine. Absent is a real answer too: the column's own default decides.
  if (typeof source.reward === 'number' && Number.isFinite(source.reward) && source.reward >= 0) {
    out.reward = source.reward;
  }
  return out;
}

/**
 * Store one family's draft. A draft with no NAME in it clears the key instead of writing, so a
 * parent who only touched the period picker leaves nothing behind — and so the write a freshly
 * mounted form makes cannot resurrect what a successful save just cleared.
 *
 * The period alone is not content: it has a default, so storing it would make every mount look
 * like unsaved work.
 *
 * @param {string} tenantId the family's own id; a missing one stores nothing
 * @param {Object} draft the whole form state; anything outside the allowlist is dropped
 */
export function writeQuestDraft(tenantId, draft) {
  const key = questDraftKey(tenantId);
  if (key === null) return;
  const kept = pick(draft);
  if (typeof kept.name !== 'string' || kept.name.trim() === '') {
    clearQuestDraft(tenantId);
    return;
  }
  try {
    window.sessionStorage.setItem(key, JSON.stringify(kept));
  } catch {
    /* Storage denied. The form still works; only the draft is lost. */
  }
}

/**
 * Read one family's draft back, merged onto the empty shape so every field is a defined value of
 * the right type.
 *
 * @param {string} tenantId the family's own id; a missing one reads nothing
 * @returns {{name: string, periodType: string, reward?: number}} Never throws. `reward` only
 *   when a real amount was stored.
 */
export function readQuestDraft(tenantId) {
  const key = questDraftKey(tenantId);
  if (key === null) return { ...EMPTY_QUEST_DRAFT };
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return { ...EMPTY_QUEST_DRAFT };
    return { ...EMPTY_QUEST_DRAFT, ...pick(JSON.parse(raw)) };
  } catch {
    return { ...EMPTY_QUEST_DRAFT };
  }
}

/**
 * Forget one family's draft. Called once the quest is actually on the account, and never before.
 *
 * It VERIFIES the removal, for the reason `hostDraft.js` records from a measurement: a swallowed
 * `removeItem` failure leaves stale bytes that a later mount restores over what the server stored.
 */
export function clearQuestDraft(tenantId) {
  const key = questDraftKey(tenantId);
  if (key === null) return;
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    /* Removal refused. Fall through to the overwrite below, which may still work. */
  }
  try {
    // `readQuestDraft` returns the empty draft for any falsy raw value, so an empty string is a
    // tombstone the reader cannot mistake for a name.
    if (window.sessionStorage.getItem(key)) window.sessionStorage.setItem(key, '');
  } catch {
    /* Storage is denied outright. A read will fail the same way, so nothing is restored from it. */
  }
}
